import React, { useState } from 'react';
import { useAuth } from '../auth/useAuth';
import { useGameStore } from '../store/gameStore';
import { OPEN_SIGNUP_ENABLED } from '../config/plans';

type Props = {
  onOpenAdmin?: () => void;
  initialToken?: string;
  initialEmail?: string;
  onClose?: () => void;
  /** Called after a successful login or signup (e.g. close the modal and enter the app). */
  onAuthenticated?: () => void;
};

const GoogleMark = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24" aria-hidden="true">
    <path fill="#EA4335" d="M12 10.2v3.9h5.5c-.24 1.4-1.66 4.1-5.5 4.1-3.3 0-6-2.7-6-6.1S8.7 6 12 6c1.9 0 3.1.8 3.8 1.5l2.6-2.5C16.8 3.4 14.6 2.4 12 2.4 6.9 2.4 2.7 6.6 2.7 11.7S6.9 21 12 21c5.5 0 9.1-3.9 9.1-9.3 0-.6-.1-1.1-.2-1.5H12z"/>
  </svg>
);

export const AuthPanel: React.FC<Props> = ({ onOpenAdmin, initialToken, initialEmail, onClose: _onClose, onAuthenticated }) => {
  const {
    user,
    profile,
    effectiveProfile,
    roles,
    login,
    logout,
    signupWithInvite,
    signupSelfServe,
    signInWithGoogle,
    loading,
    error,
    clearImpersonation,
  } = useAuth();

  // Two primary tabs; invite is a de-emphasized expandable below the form.
  const [tab, setTab] = useState<'signin' | 'signup'>(initialToken ? 'signup' : 'signin');
  const [showInvite, setShowInvite] = useState<boolean>(Boolean(initialToken));
  const [email, setEmail] = useState(initialEmail || '');
  const [password, setPassword] = useState('');
  const [inviteToken, setInviteToken] = useState(initialToken || '');
  const [message, setMessage] = useState<string | null>(null);

  const canAccessAdmin = roles.some(r => ['admin', 'dev'].includes(r));
  const isImpersonating = Boolean(
    effectiveProfile && profile && effectiveProfile.uid !== profile.uid,
  );

  const finishNewAccount = () => {
    useGameStore.setState({ showTutorial: true });
    onAuthenticated?.();
  };

  const handleGoogle = async () => {
    setMessage(null);
    try {
      const isNewAccount = await signInWithGoogle();
      if (isNewAccount) {
        finishNewAccount();
      } else {
        onAuthenticated?.();
      }
    } catch (err) {
      setMessage(err instanceof Error ? err.message : 'Google sign-in failed');
    }
  };

  const handleSignin = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage(null);
    try {
      await login(email.trim(), password);
      setPassword('');
      onAuthenticated?.();
    } catch (err) {
      setMessage(err instanceof Error ? err.message : 'Login failed');
    }
  };

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage(null);
    if (inviteFieldShown && !inviteToken.trim()) {
      setMessage(inviteRequired
        ? 'Signups are currently invite-only. Enter your invite code to continue.'
        : 'Enter your invite code, or close the invite field to create a free account.');
      return;
    }
    try {
      if (inviteFieldShown) {
        await signupWithInvite({ email: email.trim(), password, token: inviteToken.trim() });
      } else {
        await signupSelfServe({ email: email.trim(), password });
      }
      setPassword('');
      setInviteToken('');
      finishNewAccount();
    } catch (err) {
      setMessage(err instanceof Error ? err.message : 'Signup failed');
    }
  };

  if (user) {
    return (
      <div className="bg-gray-800/70 border border-gray-700 rounded-xl p-4 text-sm text-white space-y-2">
        <div className="flex items-center justify-between gap-2">
          <div>
            <div className="font-semibold">Signed in</div>
            <div className="text-gray-300">
              {effectiveProfile?.email || profile?.email || user.email}
            </div>
          </div>
          <button
            onClick={logout}
            className="px-3 py-1.5 rounded-lg bg-gray-700 hover:bg-gray-600 transition-colors text-sm font-semibold"
          >
            Logout
          </button>
        </div>

        <div className="text-gray-300">Roles: {roles.join(', ') || 'user'}</div>

        {isImpersonating && (
          <div className="bg-amber-900/40 border border-amber-500/50 rounded-lg p-2">
            <div className="text-amber-200 font-semibold">Impersonating</div>
            <div className="text-amber-100 text-xs">
              Effective: {effectiveProfile?.email} ({effectiveProfile?.uid})
            </div>
            <button
              onClick={clearImpersonation}
              className="mt-2 px-2 py-1 rounded bg-amber-700 hover:bg-amber-600 text-xs font-semibold"
            >
              Exit impersonation
            </button>
          </div>
        )}

        <div className="flex flex-wrap gap-2">
          {canAccessAdmin && onOpenAdmin && (
            <button
              onClick={onOpenAdmin}
              className="px-3 py-1.5 rounded-lg bg-emerald-700 hover:bg-emerald-600 text-sm font-semibold transition-colors"
            >
              Open Admin Panel
            </button>
          )}
        </div>

        {message && <div className="text-xs text-emerald-300">{message}</div>}
        {error && <div className="text-xs text-red-400">{error}</div>}
      </div>
    );
  }

  const isSignup = tab === 'signup';
  // Self-serve email/password + Google are only offered when open signup is on.
  // The invite path is always available (partners/trials) via the expander.
  const selfServeOpen = OPEN_SIGNUP_ENABLED;
  // When self-serve is closed, account creation is invite-only: the invite field
  // is forced on and signup routes through the invite flow.
  const inviteRequired = !selfServeOpen;
  const inviteFieldShown = showInvite || inviteRequired;

  return (
    <div className="bg-gradient-to-b from-green-900/40 to-gray-900/80 border border-amber-500/20 rounded-2xl p-5 text-white shadow-2xl">
      <div className="text-center mb-4">
        <div className="font-cinzel text-xl font-bold gold-shimmer">
          {isSignup ? 'Create your account' : 'Welcome back'}
        </div>
        <div className="text-xs text-amber-200/60 tracking-widest uppercase mt-1">JK Casino • Blackjack Trainer</div>
      </div>

      {/* Tabs */}
      <div className="flex p-1 mb-4 rounded-full bg-black/40 border border-white/10">
        <button
          type="button"
          onClick={() => { setTab('signin'); setPassword(''); }}
          className={`flex-1 px-4 py-2 rounded-full text-sm font-bold transition-colors ${
            !isSignup ? 'bg-gradient-to-r from-amber-500 to-amber-600 text-black' : 'text-white/70 hover:text-white'
          }`}
        >
          Sign in
        </button>
        <button
          type="button"
          onClick={() => { setTab('signup'); setPassword(''); }}
          className={`flex-1 px-4 py-2 rounded-full text-sm font-bold transition-colors ${
            isSignup ? 'bg-gradient-to-r from-amber-500 to-amber-600 text-black' : 'text-white/70 hover:text-white'
          }`}
        >
          Create account
        </button>
      </div>

      {selfServeOpen && (
        <>
          <button
            type="button"
            onClick={handleGoogle}
            disabled={loading}
            className="w-full flex items-center justify-center gap-3 px-4 py-2.5 rounded-xl bg-white text-gray-800 font-semibold hover:bg-gray-100 transition-colors disabled:opacity-60"
          >
            <GoogleMark />
            Continue with Google
          </button>

          <div className="flex items-center gap-3 my-4">
            <div className="h-px flex-grow bg-white/10" />
            <span className="text-xs text-white/40 uppercase tracking-wider">or</span>
            <div className="h-px flex-grow bg-white/10" />
          </div>
        </>
      )}

      <form onSubmit={isSignup ? handleSignup : handleSignin} className="space-y-3">
        <div className="space-y-1">
          <label className="block text-xs uppercase tracking-wide text-white/50">Email</label>
          <input
            type="email"
            required
            autoComplete="email"
            className="w-full px-3 py-2.5 rounded-xl bg-black/40 border border-white/10 text-white focus:border-amber-500/50 focus:outline-none transition-colors"
            value={email}
            onChange={e => setEmail(e.target.value)}
          />
        </div>
        <div className="space-y-1">
          <label className="block text-xs uppercase tracking-wide text-white/50">Password</label>
          <input
            type="password"
            required
            minLength={isSignup ? 6 : undefined}
            autoComplete={isSignup ? 'new-password' : 'current-password'}
            className="w-full px-3 py-2.5 rounded-xl bg-black/40 border border-white/10 text-white focus:border-amber-500/50 focus:outline-none transition-colors"
            value={password}
            onChange={e => setPassword(e.target.value)}
          />
          {isSignup && <p className="text-xs text-white/40">At least 6 characters.</p>}
        </div>

        {/* Invite expander — only relevant when creating an account */}
        {isSignup && (
          <div>
            {inviteFieldShown ? (
              <div className="space-y-1">
                <label className="block text-xs uppercase tracking-wide text-amber-300/70">Invite code</label>
                <input
                  className="w-full px-3 py-2.5 rounded-xl bg-black/40 border border-amber-500/30 text-white focus:border-amber-500/60 focus:outline-none transition-colors"
                  value={inviteToken}
                  onChange={e => setInviteToken(e.target.value)}
                  placeholder="Paste your invite code"
                />
                {inviteRequired && <p className="text-xs text-white/40">Signups are currently invite-only.</p>}
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setShowInvite(true)}
                className="text-xs text-amber-300/70 hover:text-amber-300 underline underline-offset-2"
              >
                Have an invite code?
              </button>
            )}
          </div>
        )}

        <button
          type="submit"
          disabled={loading}
          className="w-full px-4 py-2.5 rounded-xl bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-black font-bold shadow-lg shadow-amber-500/20 transition-all disabled:opacity-60"
        >
          {loading ? 'Working…' : isSignup ? (inviteFieldShown ? 'Create account with invite' : 'Create account') : 'Sign in'}
        </button>
      </form>

      {message && <div className="text-xs text-emerald-300 mt-3">{message}</div>}
      {error && <div className="text-xs text-red-400 mt-3">{error}</div>}
    </div>
  );
};
